/**
 * STAHLFRONT buildings — 11-family MVP catalog per spec §5.
 * Each family carries a growth-class which controls cost-curve steepness (§5.1)
 * plus a metadata bundle (display name, accent OKLCH hue, lore prose, tech-gate
 * effect lines, primary/secondary stat labels + base values + per-level growth)
 * used by the §6.1 info popup.
 *
 * All lore + label copy is original prose; no commercial-product strings reused.
 */
export type BuildingFamily =
  | "BARRACKS"
  | "RESEARCH_CENTER"
  | "RESOURCE_FACTORY"
  | "ARMORY"
  | "OFFICER_HQ"
  | "GROUND_FORCES_BASE"
  | "AIR_FORCE_BASE"
  | "VEHICLE_HANGARS"
  | "SALVAGE_YARD"
  | "AA_BASE"
  | "MILITARY_CENTER";

export const BUILDING_FAMILIES: readonly BuildingFamily[] = [
  "BARRACKS",
  "RESEARCH_CENTER",
  "RESOURCE_FACTORY",
  "ARMORY",
  "OFFICER_HQ",
  "GROUND_FORCES_BASE",
  "AIR_FORCE_BASE",
  "VEHICLE_HANGARS",
  "SALVAGE_YARD",
  "AA_BASE",
  "MILITARY_CENTER",
] as const;

/** §5.1: three growth classes tuned per building family. */
export type GrowthClass = "support" | "force_housing" | "stat_engine";

export const GROWTH_FACTOR: Readonly<Record<GrowthClass, number>> = {
  support: 1.18,
  force_housing: 1.25,
  stat_engine: 1.32,
};

export const BUILDING_GROWTH: Readonly<Record<BuildingFamily, GrowthClass>> = {
  BARRACKS: "force_housing",
  RESEARCH_CENTER: "stat_engine",
  RESOURCE_FACTORY: "support",
  ARMORY: "support",
  OFFICER_HQ: "stat_engine",
  GROUND_FORCES_BASE: "force_housing",
  AIR_FORCE_BASE: "force_housing",
  VEHICLE_HANGARS: "support",
  SALVAGE_YARD: "support",
  AA_BASE: "stat_engine",
  MILITARY_CENTER: "stat_engine",
};

export const BUILDING_LEVEL_MIN = 1 as const;
export const BUILDING_LEVEL_MAX_MVP = 32 as const;

/**
 * §5.1: cost(N) = base_cost × growth_factor^N, where N is the *target* level - 1.
 * E.g. upgrading from Lv1 → Lv2 costs cost(1); Lv5 → Lv6 costs cost(5).
 * Level 0 (called as the absolute base) returns base_cost untouched.
 *
 * @throws RangeError when level or baseCost are negative or non-finite.
 */
export function buildingUpgradeCost(
  family: BuildingFamily,
  baseCost: number,
  level: number,
): number {
  if (level < 0 || !Number.isFinite(level)) {
    throw new RangeError(`level must be a finite non-negative number, got ${level}`);
  }
  if (baseCost < 0 || !Number.isFinite(baseCost)) {
    throw new RangeError(`baseCost must be a finite non-negative number, got ${baseCost}`);
  }
  const cls = BUILDING_GROWTH[family];
  const g = GROWTH_FACTOR[cls];
  return Math.round(baseCost * g ** level);
}

/* ── §5 / §6.1 metadata catalog ───────────────────────────────────────────── */

/** Per-family info popup metadata. Pure-TS, server-authoritative; client just renders. */
export interface BuildingMeta {
  /** Human-readable building family name shown in popup title bar. */
  readonly displayName: string;
  /**
   * OKLCH hue (0..360°) owning this family's slot of palette §3.7.
   * Combine with `oklch(58% 0.16 <hue>)` for accent fills, `oklch(78% 0.04 <hue>)` for tints.
   * All 11 hues are spaced by 33° on the colour wheel for high-contrast rapid identification.
   */
  readonly accentHue: number;
  /** 2-4 sentences of original prose describing role + flavour. Renders in §6.1 lore band. */
  readonly lore: string;
  /** Numbered effect rows for the §6.1 tech-gate list. 1-3 lines per family at MVP. */
  readonly techGates: readonly string[];
  /** Row-2 label, e.g. "AA firepower", "Training cap", "Slot throughput". */
  readonly primaryStatLabel: string;
  /** Row-2 unit suffix appended to the rounded value, e.g. "" or "/h" or "men". */
  readonly primaryStatUnit: string;
  /** Row-3 label, e.g. "Harassment-fire rating", "Air-superiority bonus". */
  readonly secondaryStatLabel: string;
  readonly secondaryStatUnit: string;
  /** Stat values at Lv1 (the seeded backfill level). */
  readonly basePrimary: number;
  readonly baseSecondary: number;
  /** Per-level multiplier for both stats. Mirrors growth class strength. */
  readonly statGrowth: number;
  /** Row-4 defence multiplier % (constant per family at MVP, lifts post-Phase-3). */
  readonly defenceMultPct: number;
}

export const BUILDING_INFO: Readonly<Record<BuildingFamily, BuildingMeta>> = {
  // hue 0° red — infantry aggression
  BARRACKS: {
    displayName: "Barracks",
    accentHue: 0,
    lore:
      "Quartering halls, parade hard-stand and a small drill yard mass-produce infantry stacks for the front. Each barracks holds exactly one squad type at any time; the kind is locked when a stack is first prepared.",
    techGates: [
      "Lv 5 unlocks a second training bench, halving cap-fill time on full pours.",
      "Once the Field Doctrine research node is completed, infantry trained here gain a permanent +6% firepower bonus.",
    ],
    primaryStatLabel: "Training cap",
    primaryStatUnit: "men",
    secondaryStatLabel: "Training speed",
    secondaryStatUnit: "%",
    basePrimary: 24,
    baseSecondary: 100,
    statGrowth: 1.25,
    defenceMultPct: 130,
  },
  // hue 33° rust orange — old steel
  SALVAGE_YARD: {
    displayName: "Salvage Yard",
    accentHue: 33,
    lore:
      "Crusher, magnet crane and a sorted scrap field disassemble retired units, vehicles and parts back into raw material bundles. Four operational modes route the recovered tonnage differently.",
    techGates: [
      "Lv 6 unlocks the second salvage mode (Officer-skill recovery instead of materials).",
      "Once the Recyclage Cadre node is completed, every mode pays out an additional +10% raw-material bonus.",
    ],
    primaryStatLabel: "Mode unlocks",
    primaryStatUnit: "",
    secondaryStatLabel: "Refund rate",
    secondaryStatUnit: "%",
    basePrimary: 1,
    baseSecondary: 60,
    statGrowth: 1.18,
    defenceMultPct: 100,
  },
  // hue 66° industrial amber gold
  RESOURCE_FACTORY: {
    displayName: "Resource Factory",
    accentHue: 66,
    lore:
      "Industrial benches, drop forges and assembly lines convert raw stockpile into usable bundles, crates and drafts. Workshop slots run in parallel and accept queued orders.",
    techGates: [
      "Lv 5 / Lv 10 / Lv 20 / Lv 32 unlock a second, third, fourth and fifth parallel workshop slot.",
      "Once the Logistics Corps node is completed, every workshop slot gains a flat -8% craft duration.",
    ],
    primaryStatLabel: "Workshop slots",
    primaryStatUnit: "",
    secondaryStatLabel: "Slot throughput",
    secondaryStatUnit: "/h",
    basePrimary: 1,
    baseSecondary: 80,
    statGrowth: 1.18,
    defenceMultPct: 110,
  },
  // hue 99° yellow-green — drill ground
  MILITARY_CENTER: {
    displayName: "Military Center",
    accentHue: 99,
    lore:
      "Parade ground, drill platforms and a tactics lecture hall host unit-training drills and the tactic-system progression tree. Drills consume time and treasury but yield permanent unit buffs.",
    techGates: [
      "Lv 8 unlocks a second concurrent drill slot; Lv 22 opens a third.",
      "Once the Combat Manual node is completed, drill output gains +20% experience for every unit kind passing through the center.",
    ],
    primaryStatLabel: "Drill slots",
    primaryStatUnit: "",
    secondaryStatLabel: "Tactic-tier cap",
    secondaryStatUnit: "",
    basePrimary: 1,
    baseSecondary: 4,
    statGrowth: 1.32,
    defenceMultPct: 120,
  },
  // hue 132° green — army green
  GROUND_FORCES_BASE: {
    displayName: "Ground Forces base",
    accentHue: 132,
    lore:
      "Motor pool, fuel bowsers and a small repair shed quarter the player's armoured, mechanised and artillery stacks. Slot count of attached barracks scales with this building's level.",
    techGates: [
      "Lv 6 / Lv 14 / Lv 24 unlock a fourth, fifth and sixth attached Barracks slot for ground stacks.",
      "Once the Mechanised Doctrine node is completed, armoured stacks quartered here gain +5% strategic march speed.",
    ],
    primaryStatLabel: "Ground stack cap",
    primaryStatUnit: "",
    secondaryStatLabel: "March-speed bonus",
    secondaryStatUnit: "%",
    basePrimary: 3,
    baseSecondary: 0,
    statGrowth: 1.25,
    defenceMultPct: 125,
  },
  // hue 165° teal — mechanic
  VEHICLE_HANGARS: {
    displayName: "Vehicle Hangars",
    accentHue: 165,
    lore:
      "Long arched hangars store and refit ground vehicles outside the active garrison. A vehicle parked here regenerates parts wear and consumes no provisions.",
    techGates: [
      "Lv 5 doubles parts-wear regeneration rate inside the hangar.",
      "Once the Maintenance Doctrine node is completed, vehicles unparked from the hangar enter combat with a one-time +8% HP buff for 4 hours.",
    ],
    primaryStatLabel: "Vehicle slots",
    primaryStatUnit: "",
    secondaryStatLabel: "Parts regen rate",
    secondaryStatUnit: "%",
    basePrimary: 6,
    baseSecondary: 100,
    statGrowth: 1.18,
    defenceMultPct: 105,
  },
  // hue 198° cyan — sky
  AIR_FORCE_BASE: {
    displayName: "Air Force base",
    accentHue: 198,
    lore:
      "Concrete runway, control tower with rotating radar and dispersed aircraft hangars host fighter, bomber, recon and transport squadrons. Surrounded by AA emplacements that auto-engage hostiles.",
    techGates: [
      "Lv 8 / Lv 18 / Lv 30 unlock a second, third and fourth attached Hangar slot for air stacks.",
      "Once the Radar Network node is completed, the control tower's detection range extends by 2 map cells per Air Force base level.",
    ],
    primaryStatLabel: "Air stack cap",
    primaryStatUnit: "",
    secondaryStatLabel: "Air-superiority bonus",
    secondaryStatUnit: "%",
    basePrimary: 2,
    baseSecondary: 6,
    statGrowth: 1.25,
    defenceMultPct: 115,
  },
  // hue 231° blue — knowledge
  RESEARCH_CENTER: {
    displayName: "Research Center",
    accentHue: 231,
    lore:
      "Drafting tables, slide rules and small-batch test rigs translate combat reports into tech-tree progress. Output is the player's only source of permanent army-wide upgrades.",
    techGates: [
      "Lv 10 opens a second parallel research slot; Lv 25 opens a third.",
      "Once the Engineering Cadre node is completed, every active research slot gains a +12% throughput multiplier.",
    ],
    primaryStatLabel: "Parallel slots",
    primaryStatUnit: "",
    secondaryStatLabel: "Speed multiplier",
    secondaryStatUnit: "%",
    basePrimary: 1,
    baseSecondary: 100,
    statGrowth: 1.32,
    defenceMultPct: 110,
  },
  // hue 264° indigo — steel-violet
  ARMORY: {
    displayName: "Armory",
    accentHue: 264,
    lore:
      "Reinforced racks, locked vaults and a hardened bunker shell house the player's weapons and ammunition reserves. Stockpile up to the protected cap is shielded from raid losses.",
    techGates: [
      "Lv 8 doubles the protected stockpile cap; Lv 20 doubles it again.",
      "Once the Quartermaster Corps node is completed, raids that exceed the protected cap forfeit only 70% of overflow instead of the default 85%.",
    ],
    primaryStatLabel: "Protected stockpile",
    primaryStatUnit: "",
    secondaryStatLabel: "Overflow shield",
    secondaryStatUnit: "%",
    basePrimary: 50000,
    baseSecondary: 15,
    statGrowth: 1.18,
    defenceMultPct: 160,
  },
  // hue 297° purple — anti-air
  AA_BASE: {
    displayName: "AA Base",
    accentHue: 297,
    lore:
      "Sandbagged platforms, a quad-mount autocannon and tracking optics defend the base from incoming bombers and harass enemy ground formations within range. The flagship stat-engine of the home base.",
    techGates: [
      "Lv 30 / 31 / 32 extend harassment-fire range against ground targets to 5, 7 and 9 map cells.",
      "Once the City Defence node is completed, AA Bases automatically engage every bomber attacking the base regardless of stance.",
      "Once the Close Defence node is completed, the AA-base damage multiplier inside the base-defence radius increases by +35%.",
    ],
    primaryStatLabel: "AA firepower",
    primaryStatUnit: "",
    secondaryStatLabel: "Harassment-fire rating",
    secondaryStatUnit: "",
    basePrimary: 12000,
    baseSecondary: 8000,
    statGrowth: 1.32,
    defenceMultPct: 140,
  },
  // hue 330° magenta — command
  OFFICER_HQ: {
    displayName: "Officer HQ",
    accentHue: 330,
    lore:
      "A staff hall, briefing rooms and a personnel registry where the player promotes, assigns and retires command staff. Every commissioned officer takes one HQ slot.",
    techGates: [
      "Lv 10 raises the maximum promotion rank from Major to Colonel.",
      "Once the Staff College node is completed, officers gain skill-experience 15% faster while attached to a stack.",
    ],
    primaryStatLabel: "Officer slots",
    primaryStatUnit: "",
    secondaryStatLabel: "Promotion rank cap",
    secondaryStatUnit: "",
    basePrimary: 4,
    baseSecondary: 3,
    statGrowth: 1.32,
    defenceMultPct: 120,
  },
};

/**
 * Round helper used by both stat formulas to keep wire payloads compact.
 *
 * @throws RangeError when `level` is < 1 or non-finite.
 */
function applyGrowth(base: number, growth: number, level: number): number {
  if (level < 1 || !Number.isFinite(level)) {
    throw new RangeError(`level must be ≥ 1, got ${level}`);
  }
  return Math.round(base * growth ** (level - 1));
}

/** §5.1 primary stat at the given level — used by the §6.1 popup row 2. */
export function buildingPrimaryStat(family: BuildingFamily, level: number): number {
  const meta = BUILDING_INFO[family];
  return applyGrowth(meta.basePrimary, meta.statGrowth, level);
}

/** §5.1 secondary stat at the given level — used by the §6.1 popup row 3. */
export function buildingSecondaryStat(family: BuildingFamily, level: number): number {
  const meta = BUILDING_INFO[family];
  return applyGrowth(meta.baseSecondary, meta.statGrowth, level);
}

/**
 * §5.2 placeholder power contribution per spec column.
 * Power = round((basePrimary*0.5 + baseSecondary*0.3) * statGrowth^(level-1)).
 * Real spec §4.2 power model lands later; this keeps the level breakdown
 * modal honest about the rough scale until then.
 *
 * @throws RangeError on level < 1.
 */
export function buildingPower(family: BuildingFamily, level: number): number {
  if (level < 1 || !Number.isFinite(level)) {
    throw new RangeError(`level must be ≥ 1, got ${level}`);
  }
  const meta = BUILDING_INFO[family];
  const base = meta.basePrimary * 0.5 + meta.baseSecondary * 0.3;
  return Math.round(base * meta.statGrowth ** (level - 1));
}

/* ── §5.1 / §6.4.3: building upgrade cost + duration ─────────────────────── */

/**
 * Per-class baseline upgrade cost in treasury (from Lv 1 to Lv 2 reference).
 * Real cost grows as `baseUpgradeCost × statGrowth^(fromLevel - 1)`.
 */
export const BASE_UPGRADE_COST: Readonly<Record<GrowthClass, number>> = {
  support: 800,
  force_housing: 1500,
  stat_engine: 2400,
};

/**
 * Per-class baseline upgrade duration (Lv 1 -> Lv 2 reference). Grows slower
 * than cost so late-game waits stay tractable: `dur × statGrowth^(L-1) × 0.6`.
 */
export const BASE_UPGRADE_DURATION_MS: Readonly<Record<GrowthClass, number>> = {
  support: 30_000,
  force_housing: 45_000,
  stat_engine: 60_000,
};

/** §5.1: treasury cost to advance a building from `fromLevel` to `fromLevel + 1`.
 *  @throws RangeError on fromLevel < 1 or non-finite. */
export function buildingUpgradeCostAt(family: BuildingFamily, fromLevel: number): number {
  if (fromLevel < 1 || !Number.isFinite(fromLevel)) {
    throw new RangeError(`fromLevel must be ≥ 1, got ${fromLevel}`);
  }
  const cls = BUILDING_GROWTH[family];
  const meta = BUILDING_INFO[family];
  return Math.round(BASE_UPGRADE_COST[cls] * meta.statGrowth ** (fromLevel - 1));
}

/** §5.1: real-time duration to advance a building from `fromLevel` to `fromLevel + 1`.
 *  @throws RangeError on fromLevel < 1 or non-finite. */
export function buildingUpgradeDurationMsAt(family: BuildingFamily, fromLevel: number): number {
  if (fromLevel < 1 || !Number.isFinite(fromLevel)) {
    throw new RangeError(`fromLevel must be ≥ 1, got ${fromLevel}`);
  }
  const cls = BUILDING_GROWTH[family];
  const meta = BUILDING_INFO[family];
  return Math.max(
    1000,
    Math.round(BASE_UPGRADE_DURATION_MS[cls] * meta.statGrowth ** (fromLevel - 1) * 0.6),
  );
}
